import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from 'Button';
import { Dropdown, type SingleSectionItems } from 'Dropdown';
import { type SelectOption } from './Select.types';

type Props = {
    options: SelectOption[];
    value: string;
    onValueChange: (value: string) => void;
    'aria-label': string;
    className?: string;
    isDisabled?: boolean;
    // Rendered in the trigger when `value` matches none of the options (typically because nothing
    // has been chosen yet), in place of the raw value.
    placeholder?: string;
};

// Tracks the trigger's rendered width so the menu can match it, re-measuring since it's fluid.
function useMeasuredWidth() {
    const ref = useRef<HTMLButtonElement>(null);
    const [width, setWidth] = useState<number>();

    useEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        const observer = new ResizeObserver(() => setWidth(element.getBoundingClientRect().width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return [ref, width] as const;
}

// A themed, opinionated single-value form control built on the shared `Dropdown`. It fixes the
// trigger to a `bordered-white-light` `Button` that renders the selected option's label plus a
// chevron, and exposes a plain `string`-in / `string`-out API (`value` / `onValueChange`) instead
// of `Dropdown`'s `selectedKeys: string[]` + caller-supplied trigger surface. Use this for the
// common "pick one value" case so call sites don't re-hand-roll that boilerplate.
//
// Reach for `Dropdown` directly (not `Select`) when you need any of its flexibility: a custom
// trigger that doesn't display the selected value, multiple selection, multi-section menus, or
// controlled open state (e.g. `AudioSelectButton`'s device picker).
//
// A bare control: no label, no error message, not form-aware. In-form callers supply those and wrap
// it in a `Controller`.
export function Select({
    options,
    value,
    onValueChange,
    'aria-label': ariaLabel,
    className,
    isDisabled,
    placeholder,
}: Props) {
    const selectedOption = options.find(option => option.value === value);
    // Wrap instead of inheriting HeroUI's `truncate`, which would hide the end of a long label once
    // the menu is pinned to the trigger's width. Important modifier: their class isn't in a twMerge.
    const items: SingleSectionItems = options.map(option => ({
        key: option.value,
        label: option.label,
        classNames: { title: '!whitespace-normal' },
    }));
    const [triggerRef, menuWidth] = useMeasuredWidth();

    return (
        <Dropdown
            type="single-section"
            menuWidth={menuWidth}
            selectionMode="single"
            selectedKeys={[value]}
            onSelectionChange={([selectedKey]) => {
                if (selectedKey) onValueChange(selectedKey);
            }}
            items={items}
        >
            <Button
                variant="bordered-white-light"
                color="black"
                height={9}
                fontWeight="normal"
                aria-label={ariaLabel}
                // Lifts the Button's `min-w-max` (pushes a long label past a narrow container) and
                // HeroUI's `aria-expanded:scale-[0.97]` (shrinks the field while the menu is open).
                className={twMerge('!min-w-0 max-w-full aria-expanded:!scale-100', className)}
                isDisabled={isDisabled}
                ref={triggerRef}
            >
                <span className="truncate">{selectedOption?.label ?? placeholder ?? value}</span>
                <FontAwesomeIcon icon={faChevronDown} className="shrink-0 text-xs text-slate-grey" />
            </Button>
        </Dropdown>
    );
}
